import { createContext, ReactNode, useCallback, useContext, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useImpersonatedUserId } from '../api/authorization/impersonationStore'

export type ImpersonationValue = {
  impersonatedUserId: string | undefined
  isImpersonating: boolean
  isReadOnly: boolean
  startImpersonation: (userId: string) => void
  stopImpersonation: () => void
}

const noopImpersonation: ImpersonationValue = {
  impersonatedUserId: undefined,
  isImpersonating: false,
  isReadOnly: false,
  startImpersonation: () => {},
  stopImpersonation: () => {},
}

const ImpersonationContext = createContext<ImpersonationValue>(noopImpersonation)

export const ImpersonationProvider = ({ children }: { children: ReactNode }) => {
  const [impersonatedUserId, setImpersonatedUserId] = useImpersonatedUserId()
  const queryClient = useQueryClient()

  // Cached responses are keyed under the previous effective user — drop them so
  // permission/entity reads re-fetch as the new identity.
  const swapImpersonation = useCallback(
    (next: string | undefined) => {
      setImpersonatedUserId(next)
      queryClient.invalidateQueries()
    },
    [queryClient, setImpersonatedUserId]
  )

  const startImpersonation = useCallback((userId: string) => swapImpersonation(userId), [swapImpersonation])
  const stopImpersonation = useCallback(() => swapImpersonation(undefined), [swapImpersonation])

  const value = useMemo<ImpersonationValue>(
    () => ({
      impersonatedUserId,
      isImpersonating: impersonatedUserId !== undefined,
      isReadOnly: impersonatedUserId !== undefined,
      startImpersonation,
      stopImpersonation,
    }),
    [impersonatedUserId, startImpersonation, stopImpersonation]
  )

  return <ImpersonationContext.Provider value={value}>{children}</ImpersonationContext.Provider>
}

export const useImpersonation = (): ImpersonationValue => useContext(ImpersonationContext)
